import {
  Button,
  Card,
  Popover,
  TextStyle,
  ToggleButton,
  DateTimePicker,
  ActionList,
  ButtonGroup,
  Modal,
  Banner,
  DisplayText
} from '@superops/ui-design-system';
import React, { useContext, useEffect, useState } from 'react';
import SchedulingContext from '../Contexts/SchedulingContext';
import { SVGIcon } from '@superops/super-icons';
import { CALENDAR_VIEWS } from '../helpers';
import {
  getDateTimeFormat,
  DateTimeFormatter,
  WeekPicker,
  CustomModal,
  FeatureGuide,
  Row
} from '@nile/components';
import dayjs from 'dayjs';
import { FormattedMessage } from 'react-intl';
import EventTemplate from './EventTemplate';
import CalendarSettings from './CalendarSettings';
import { useMutation } from '@apollo/client';
import { RESYNC_CALENDAR_SUBSCRIPTION, TicketClient } from '@nile/graphql';
import isoWeek from 'dayjs/plugin/isoWeek';
import enGb from 'date-fns/locale/en-GB';
import { registerLocale } from 'react-datepicker';
dayjs.extend(isoWeek);
registerLocale('enGb', enGb);

const AVAILABLE_VIEWS = [
  { name: 'day', label: <FormattedMessage id="common.day" />, value: 'day' },
  { name: 'week', label: <FormattedMessage id="common.week" />, value: 'week' }
];

const FILTER_OPTIONS = [
  {
    name: 'all',
    label: <FormattedMessage id="scheduling.toolbar.filter.all.events" />,
    value: 'all',
    icon: 'DatePicker',
    default: true
  },
  {
    name: 'ticket',
    label: <FormattedMessage id="scheduling.toolbar.filter.tickets.event" />,
    value: 'ticket',
    icon: 'TicketIdentifier',
    module: 'TICKET'
  },
  {
    name: 'project',
    label: <FormattedMessage id="scheduling.toolbar.filter.projects.event" />,
    value: 'project',
    icon: 'ProjectIdentifier',
    module: 'PROJECT'
  }
];

const MORE_SETTING_OPTIONS = [
  {
    name: 'eventTemplate',
    label: (
      <FormattedMessage id="scheduling.toolbar.settings.event.template.label" />
    ),
    icon: 'Tasks'
  },
  {
    name: 'calendarSettings',
    label: (
      <FormattedMessage id="scheduling.toolbar.settings.calendar.settings.label" />
    ),
    icon: 'CalendarSettings'
  }
];

const SchedulingToolbar = (props) => {
  const [datepickerActive, setDatepickerActive] = useState(false);
  const [viewPickerActive, setViewPickerActive] = useState(false);
  const [filterOptionsActive, setFilterOptionsActive] = useState(false);
  const [filterType, setFilterType] = useState(
    FILTER_OPTIONS.find((el) => el?.default === true)
  );
  const [moreSettingsActive, setMoreSettingsActive] = useState(false);
  const [eventTemplateSheetActive, setEventTemplateSheetActive] = useState(
    false
  );
  const [
    calendarSettingsSheetActive,
    setCalendarSettingsSheetActive
  ] = useState(false);

  const schedulingContext = useContext(SchedulingContext);

  const [isResyncSubscriptionDone, setResyncSubscriptionDone] = useState(false);

  const [
    isUpdatingResyncSubscription,
    setIsUpdatingResyncSubscription
  ] = useState(false);

  const togglePrevWeek = () => {
    const currentDay = dayjs(schedulingContext?.selectedDate || new Date());
    const prevWeekDay = currentDay.subtract(1, 'day').toDate();
    schedulingContext?.setSelectedDate(prevWeekDay);
  };

  const toggleNextWeek = () => {
    const currentDay = dayjs(schedulingContext?.selectedDate || new Date());
    const nextDay = currentDay.add(1, 'day').toDate();
    schedulingContext?.setSelectedDate(nextDay);
  };

  const toggleDispatcherView = (bool) => {
    schedulingContext?.setDispatcherView(bool);
    schedulingContext?.setCalendarView('day');
    schedulingContext?.setSelectedDate(new Date());
    schedulingContext?.setSelectedWeekRange(null);
  };

  const handleFilterChange = (el) => {
    let filterCondition = null;
    setFilterType(el);
    if (el?.name === 'ticket') {
      filterCondition = {
        attribute: 'workItem.module',
        operator: 'is',
        value: 'TICKET'
      };
    } else if (el?.name === 'project') {
      filterCondition = {
        attribute: 'workItem.module',
        operator: 'is',
        value: 'PROJECT'
      };
    }
    schedulingContext?.setFilterConditions(filterCondition);
  };

  const isPrevDayDisabled = () => {
    return (
      dayjs(schedulingContext?.selectedDate).format('YYYY-MM-DD') ===
      dayjs(schedulingContext?.integratedDate).format('YYYY-MM-DD')
    );
  };

  const isShowLastWeek = () => {
    // Function to show last week conditionally in week filter
    const currentDate = dayjs();
    const integratedDate = dayjs(schedulingContext?.integratedDate); // Convert provided date to dayjs object;

    const currentIsoWeek = currentDate.isoWeek();
    const integratedIsoWeek = integratedDate.isoWeek();

    if (currentDate.day() === 0 && integratedIsoWeek === currentIsoWeek + 1) {
      // We use mon - sun but in Js it's sun - sat
      // So last week should not be available in integrated week sunday
      return false;
    } else {
      // If current week and integrated week is same we don't show last week
      return currentDate.isoWeek() !== integratedDate.isoWeek();
    }
  };

  const showLastWeek = isShowLastWeek();

  const handleMoreSettings = (data) => {
    if (data?.name === 'eventTemplate') {
      setEventTemplateSheetActive(true);
    }
    if (data?.name === 'calendarSettings') {
      setCalendarSettingsSheetActive(true);
    }
  };

  const onSettingsClick = () => {
    const isDispatcher = schedulingContext?.isDispatcher;
    if (isDispatcher) {
      setMoreSettingsActive(!moreSettingsActive);
    } else {
      setCalendarSettingsSheetActive(true);
    }
  };

  const getIsSelectedDateToday = (inputDate) => {
    const currentDate = dayjs();
    const parsedInputDate = dayjs(inputDate);
    const isToday = parsedInputDate.isSame(currentDate, 'day');
    return isToday;
  };

  const isToday = getIsSelectedDateToday(schedulingContext?.selectedDate);

  const handleFilterChangeOnMount = (currentFilter) => {
    const module = currentFilter?.value;
    if (module) {
      const filterData = FILTER_OPTIONS.find((el) => el?.module === module);
      setFilterType(filterData);
    }
  };

  useEffect(() => {
    handleFilterChangeOnMount(schedulingContext?.filterConditions);
  }, []);

  const [reSyncCalendarSubscription] = useMutation(
    RESYNC_CALENDAR_SUBSCRIPTION,
    {
      client: TicketClient,
      onCompleted(data) {
        if (data?.reSyncCalendarSubscription) {
          setResyncSubscriptionDone(
            data.reSyncCalendarSubscription.syncStatus === 'SUCCESS'
          );
        }
      }
    }
  );

  function handleSync() {
    setIsUpdatingResyncSubscription(true);
    reSyncCalendarSubscription().finally(() => {
      setIsUpdatingResyncSubscription(false);
    });
  }

  return (
    <>
      {/* <Card> */}
      <div
        // className="height100 flex actr pl50 pr-50 "
        className="scheduling-toolbar"
        id="scheduling-toolbar"
      >
        <div className="w100 flex fx-sb actr">
          <div className="flex g10 actr">
            {schedulingContext?.isDispatcher ? (
              <div className="flex actr g10">
                {schedulingContext?.dispatcherView && (
                  <Button
                    icon={<SVGIcon name="CustomViewsPanel" />}
                    onClick={schedulingContext?.toggleTechnicianSheet}
                  />
                )}
                <div>
                  <ButtonGroup segmented>
                    <Button
                      outline={!schedulingContext?.dispatcherView}
                      selected={!schedulingContext?.dispatcherView}
                      onClick={() => {
                        toggleDispatcherView(false);
                      }}
                      disabled={props?.dispatcherLoading}
                    >
                      <FormattedMessage id="common.my.calendar" />
                    </Button>
                    <Button
                      outline={schedulingContext?.dispatcherView}
                      selected={schedulingContext?.dispatcherView}
                      onClick={() => {
                        toggleDispatcherView(true);
                      }}
                    >
                      <FormattedMessage id="common.dispatcher" />
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            ) : (
              <TextStyle weight="SemiBold">
                <span className="f-24">
                  <FormattedMessage id="common.my.calendar" />
                </span>
              </TextStyle>
            )}
            {!props?.hideCalendarSettings && (
              <div>
                <Popover
                  activator={
                    <div className="more-settings" onClick={onSettingsClick}>
                      <SVGIcon name="Actions" />
                    </div>
                  }
                  active={moreSettingsActive}
                  onClose={() => setMoreSettingsActive(!moreSettingsActive)}
                  zIndexOverride={600}
                >
                  <ActionList
                    items={MORE_SETTING_OPTIONS.map((el) => ({
                      content: (
                        <div
                          className="flex g10"
                          onClick={() => {
                            handleMoreSettings(el);
                            setMoreSettingsActive(false);
                          }}
                        >
                          <div>
                            <SVGIcon name={el?.icon} />
                          </div>
                          <div>
                            <TextStyle>
                              <span className="capitalize">{el?.label}</span>
                            </TextStyle>
                          </div>
                        </div>
                      )
                    }))}
                  />
                </Popover>
              </div>
            )}
            <div id="scheduling-feature-guide">
              <FeatureGuide featureName="scheduling" article video />
            </div>
          </div>

          <div className="flex g20 actr">
            <div id="sla-toggle">
              <div className="flex g10 actr">
                <TextStyle variation="subdued" weight="Regular">
                  <div className="f-14 lh-unset">
                    <FormattedMessage id="scheduling.toolbar.showSla" />
                  </div>
                </TextStyle>
                <ToggleButton
                  size="small"
                  value={schedulingContext?.showSla}
                  onChange={(val) => schedulingContext?.setShowSla(val)}
                />
              </div>
            </div>
            <div id="filter">
              <Popover
                activator={
                  <Button
                    icon={<SVGIcon name={filterType?.icon} />}
                    onClick={() => setFilterOptionsActive(!filterOptionsActive)}
                  >
                    <div className="flex actr g10">
                      <div>
                        <TextStyle weight="Medium">
                          {filterType?.label}
                        </TextStyle>
                      </div>
                      <div className="flex">
                        <SVGIcon name="ArrowDown" />
                      </div>
                    </div>
                  </Button>
                }
                active={filterOptionsActive}
                onClose={() => setFilterOptionsActive(!filterOptionsActive)}
                zIndexOverride={600}
              >
                <ActionList
                  items={FILTER_OPTIONS.map((el) => ({
                    content: (
                      <div
                        onClick={() => {
                          handleFilterChange(el);
                          setFilterOptionsActive(!filterOptionsActive);
                        }}
                        key={el?.label}
                        className="flex g10"
                        icon={<SVGIcon name={el?.icon} />}
                      >
                        <div>
                          <TextStyle>{el?.label}</TextStyle>
                        </div>
                      </div>
                    )
                  }))}
                />
              </Popover>
            </div>

            {!schedulingContext?.dispatcherView && (
              <div id="calendar-view-select">
                <Popover
                  activator={
                    <Button
                      icon={<SVGIcon name="DatePicker" />}
                      onClick={() => setViewPickerActive(!viewPickerActive)}
                    >
                      <div className="flex g10 actr">
                        <div className="capitalize">
                          <TextStyle weight="Medium">
                            {
                              AVAILABLE_VIEWS.find(
                                (el) =>
                                  el?.value === schedulingContext?.calendarView
                              )?.label
                            }
                          </TextStyle>
                        </div>
                        <div className="flex">
                          <SVGIcon name="ArrowDown" />
                        </div>
                      </div>
                    </Button>
                  }
                  active={viewPickerActive}
                  onClose={() => setViewPickerActive(!viewPickerActive)}
                  zIndexOverride={600}
                >
                  <ActionList
                    items={AVAILABLE_VIEWS.map((el) => ({
                      content: (
                        <div
                          className="min-w80"
                          onClick={() => {
                            schedulingContext?.handleCalendarViewChange(
                              el?.value
                            );
                            setViewPickerActive(false);
                          }}
                        >
                          <TextStyle>
                            <span className="capitalize">{el?.label}</span>
                          </TextStyle>
                        </div>
                      )
                    }))}
                  />
                </Popover>
              </div>
            )}

            <div id="datepicker">
              {schedulingContext?.calendarView === CALENDAR_VIEWS.DAY && (
                <div className="flex g10 actr">
                  <Button
                    onClick={togglePrevWeek}
                    icon={<SVGIcon name="ArrowLeft" size="sm" />}
                    disabled={isPrevDayDisabled()}
                  />
                  <Popover
                    activator={
                      <div
                        className="flex g5 actr pointer"
                        onClick={() => setDatepickerActive(!datepickerActive)}
                      >
                        <div className="mt3 fx">
                          <SVGIcon name="DatePicker" />
                        </div>
                        <div className="f-14 lh-unset ">
                          <TextStyle weight="SemiBold">
                            {isToday ? (
                              <FormattedMessage id="placeholder.today" />
                            ) : (
                              <DateTimeFormatter
                                type="date"
                                date={schedulingContext?.selectedDate}
                              />
                            )}
                          </TextStyle>
                        </div>
                      </div>
                    }
                    active={datepickerActive}
                    onClose={() => {}}
                    fullHeight
                    zIndexOverride={600}
                  >
                    <div className="react-date-picker-no-box-shadow">
                      <DateTimePicker
                        inline
                        popperPlacement="bottom"
                        shouldCloseOnSelect
                        dateFormat={getDateTimeFormat()?.dateFormat}
                        locale="enGb"
                        selectedDate={dayjs(schedulingContext?.selectedDate)}
                        selected={schedulingContext?.selectedDate}
                        onChange={(date) => {
                          setDatepickerActive(false);
                          schedulingContext?.setSelectedDate(date);
                        }}
                        minDate={new Date(schedulingContext?.integratedDate)}
                      />
                    </div>
                  </Popover>
                  <Button
                    onClick={toggleNextWeek}
                    icon={<SVGIcon name="ArrowRight" size="sm" />}
                  />
                </div>
              )}

              {schedulingContext?.calendarView === CALENDAR_VIEWS.WEEK && (
                <WeekPicker
                  selected={schedulingContext?.selectedDate}
                  onChange={schedulingContext?.onWeekChange}
                  minDate={new Date(schedulingContext?.integratedDate)}
                  previousWeekDisabled={isPrevDayDisabled()}
                  placeHoldersToRemove={!showLastWeek && ['last.week']}
                />
              )}
            </div>
            {props?.onClose && (
              <div>
                <Button
                  icon={<SVGIcon name="Close" />}
                  onClick={props?.onClose}
                />
              </div>
            )}
          </div>
        </div>
        {schedulingContext?.calendarInfo?.syncStatus === 'FAILURE' &&
          !isResyncSubscriptionDone && (
            <div
              className={`mt-20 ${isUpdatingResyncSubscription &&
                'disable-actions'}`}
            >
              <Banner status="info">
                <Row gap={10}>
                  <DisplayText element="p" size="large">
                    <FormattedMessage id="scheduling.syncError.banner.errorMessage" />
                  </DisplayText>
                  <Button plain onClick={handleSync}>
                    <FormattedMessage id="scheduling.syncErrorBanner.button.syncNow" />
                  </Button>
                </Row>
              </Banner>
            </div>
          )}
      </div>
      {/* </Card> */}

      {eventTemplateSheetActive && (
        <Modal
          open={eventTemplateSheetActive}
          onClose={() => setEventTemplateSheetActive(false)}
          large
          showCloseButton={false}
        >
          <EventTemplate
            onCancel={() => setEventTemplateSheetActive(false)}
            onClose={() => setEventTemplateSheetActive(false)}
          />
        </Modal>
      )}

      {calendarSettingsSheetActive && (
        <Modal
          open={calendarSettingsSheetActive}
          onClose={() => setCalendarSettingsSheetActive(false)}
          showCloseButton={false}
        >
          <CalendarSettings
            onCancel={() => setCalendarSettingsSheetActive(false)}
            onClose={() => setCalendarSettingsSheetActive(false)}
          />
        </Modal>
      )}
    </>
  );
};

export default SchedulingToolbar;
